import { Component, ElementRef, Input, OnDestroy, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { ToastController } from "ionic-angular";
import { UtilityService } from "providers/utility-service/utility-service";
import { AuthService } from "../../../app/auth/auth.service";
import { QaQueueService } from "../../../providers/qa-queue/qa-queue.service";
import {
  default as ActionType,
  default as VisitType,
} from "../models/action-type";

@Component({
  selector: "visit-types-grid",
  templateUrl: "visit-types-grid.html",
})
export class VisitTypesGridComponent implements OnInit, OnDestroy {
  /** CO-3507: Company:QAControls:VisitType:EDIT, resolved by the host page. Default
   *  false so the grid is read-only until IAM says otherwise (fail-closed). */
  @Input() canEdit: boolean = false;

  query: string = "";
  visitTypes: ActionType[] = [];
  isLoading: boolean = false;
  hasValidInput: boolean = false;

  visitsForm: FormGroup;
  private boundOnSearchIconClick: () => void;
  private searchIconEl: Element | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private toastCtrl: ToastController,
    private qaQueueService: QaQueueService,
    private elementRef: ElementRef,
    private authSvc: AuthService,
    private utilityService: UtilityService
  ) {
    this.visitsForm = this.formBuilder.group({});
  }

  initializeForm() {
    this.visitsForm = this.formBuilder.group({});

    this.visitTypes.forEach((item: ActionType) => {
      this.visitsForm.addControl(
        item.action,
        this.formBuilder.control(item.percentage)
      );
    });

    this.visitsForm.valueChanges.subscribe(() => {
      this.hasValidInput = Object.values(this.visitsForm.value).some(
        (value) => value !== null && value !== ""
      );
    });
  }

  ngOnInit(): void {
    this.searchResults();

    this.searchIconEl = this.elementRef.nativeElement.querySelector(
      ".searchbar-search-icon"
    );
    if (this.searchIconEl) {
      (this.searchIconEl as any).removeAllListeners?.();
      this.boundOnSearchIconClick = this.onSearchInputChange.bind(this);
      this.searchIconEl.addEventListener("click", this.boundOnSearchIconClick);
    }
  }

  ngOnDestroy(): void {
    if (this.searchIconEl && this.boundOnSearchIconClick) {
      this.searchIconEl.removeEventListener(
        "click",
        this.boundOnSearchIconClick
      );
    }
  }

  onSearchCancel(): void {
    this.query = "";
    this.searchResults();
  }

  onSearchInputChange(): void {
    this.searchResults();
  }

  onSubmit(): void {
    // Defence in depth: the disabled button is only a hint, so refuse here too.
    if (!this.canEdit) return;
    let fields = Object.keys(this.visitsForm.value);
    let payload = fields.map((field) => {
      return {
        actionType: "VISIT",
        action: field,
        percentage: this.visitsForm.value[field],
      };
    });
    let tenantId = this.authSvc.getUser().tenantId;

    this.isLoading = true;
    this.utilityService.increaseSpinnerCount("Submitting...");
    this.qaQueueService.putActionTypes(tenantId, payload).subscribe(
      (_) => {
        this.displayToaster("Submitted successfully", "success");
        this.hasValidInput = false;
      },
      (err) => {
        console.error(err);
        this.isLoading = false;
        this.utilityService.decreaseSpinnerCount();
        this.displayToaster(
          "An error occurred while storing the visit types",
          "error"
        );
      },
      () => {
        this.isLoading = false;
        this.utilityService.decreaseSpinnerCount();
      }
    );
  }

  restrictNonNumeric(event: any) {
    const input = event.target;
    const pattern = /^[0-9]*$/;

    if (!pattern.test(event.key)) {
      event.preventDefault();
    } else {
      const value = input.value + event.key;

      if (Number(value) > 100) {
        event.preventDefault();
      }
    }
  }

  onInput(event: any, controlName: string) {
    const input = event.target;
    let value = input.value;

    // Remove leading zeros
    if (value.length > 0) {
      value = Number(value);
    }
    this.visitsForm.controls[controlName].setValue(value);
  }

  displayToaster(message: string, cssClass: string) {
    let toast = this.toastCtrl.create({
      message: message,
      duration: 5000,
      position: "below",
      cssClass: cssClass,
    });
    toast.present();
  }

  searchResults() {
    let tenantId = this.authSvc.getUser().tenantId;
    this.utilityService.increaseSpinnerCount("Loading visit types...");
    this.isLoading = true;
    this.qaQueueService
      .searchQAQueueActions(
        tenantId,
        "ACTION",
        this.query,
        "actionName",
        "asc",
        100,
        1
      )
      .subscribe(
        (data) => {
          this.visitTypes = data.data as VisitType[];
          this.initializeForm();
        },
        (error) => {
          console.error(error);
          this.displayToaster(
            "An error occurred while loading visit types",
            "error"
          );
        },
        () => {
          this.utilityService.decreaseSpinnerCount();
          this.isLoading = false;
        }
      );
  }
}



// WEBPACK FOOTER //
// ./src/components/qa-control/visit-types-grid/visit-types-grid.ts